import { useMemo, useState } from "react";
import ChartTooltip from "./ChartTooltip";

function linkPath(x0, y0, h0, x1, y1, h1) {
  const mx = (x0 + x1) / 2;
  return `M${x0},${y0} C${mx},${y0} ${mx},${y1} ${x1},${y1} L${x1},${y1 + h1} C${mx},${y1 + h1} ${mx},${y0 + h0} ${x0},${y0 + h0} Z`;
}

function dropPath(x0, y0, h0, dropH, chartBottom) {
  const x1 = x0 + 24;
  const y1 = chartBottom - dropH;
  const mx = x0 + 12;
  return `M${x0},${y0 + h0} C${mx},${y0 + h0 + 4} ${mx},${y1} ${x1},${y1} L${x1},${y1 + dropH} C${mx},${y1 + dropH} ${mx},${y0 + h0 + dropH + 4} ${x0},${y0 + h0} Z`;
}

function nodeHeight(value, max, chartH) {
  return Math.max(10, (value / max) * chartH);
}

function layoutNodes(nodes, max, chartH, padTop, padX, nodeW, colGap) {
  const byCol = new Map();
  nodes.forEach((n) => {
    if (!byCol.has(n.column)) byCol.set(n.column, []);
    byCol.get(n.column).push(n);
  });

  const positioned = [];

  for (const [column, colNodes] of byCol.entries()) {
    const x = padX + column * (nodeW + colGap);
    const sorted = [...colNodes].sort((a, b) => a.row - b.row);

    if (sorted.length === 1) {
      const node = sorted[0];
      const nodeH = nodeHeight(node.value, max, chartH);
      const y = padTop + node.row * Math.max(0, chartH - nodeH);
      positioned.push({ ...node, x, y, nodeH, nodeW });
      continue;
    }

    const gap = 22;
    const heights = sorted.map((n) => nodeHeight(n.value, max, chartH));
    const totalH = heights.reduce((s, h) => s + h, 0) + gap * (sorted.length - 1);
    let cursor = padTop + Math.max(0, (chartH - totalH) / 2);

    sorted.forEach((node, i) => {
      const nodeH = heights[i];
      positioned.push({ ...node, x, y: cursor, nodeH, nodeW });
      cursor += nodeH + gap;
    });
  }

  return positioned.sort((a, b) => a.column - b.column || a.row - b.row);
}

function NodeLabels({ node, layout, clickable, onActivate }) {
  const cx = node.x + node.nodeW / 2;
  const titleY = Math.max(12, node.y - 6);
  const valueY = Math.min(layout.h - 10, node.y + node.nodeH + 14);
  const pctY = valueY + 12;
  const shortLabel = node.shortLabel || node.label;
  const labelClass = clickable ? "sankey-node-label sankey-node-label--clickable" : "sankey-node-label";

  const activate = (e) => {
    e.stopPropagation();
    onActivate?.(node);
  };

  return (
    <>
      <text
        x={cx}
        y={titleY}
        textAnchor="middle"
        fontSize="8"
        fill="var(--text-2)"
        className={labelClass}
        style={{ cursor: clickable ? "pointer" : undefined }}
        onClick={clickable ? activate : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activate(e);
                }
              }
            : undefined
        }
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
      >
        {shortLabel}
      </text>
      {node.optional && (
        <text x={cx} y={titleY + 9} textAnchor="middle" fontSize="7" fill="var(--text-3)" pointerEvents="none">
          optional
        </text>
      )}
      <text
        x={cx}
        y={valueY}
        textAnchor="middle"
        fontSize="10"
        fontWeight="600"
        fill="var(--text-1)"
        className={clickable ? "sankey-node-label--clickable" : undefined}
        style={{ cursor: clickable ? "pointer" : undefined }}
        onClick={clickable ? activate : undefined}
        pointerEvents={clickable ? "all" : "none"}
      >
        {node.value.toLocaleString()}
      </text>
      {node.pctOfSignups != null && (
        <text x={cx} y={pctY} textAnchor="middle" fontSize="8" fill="var(--text-3)" pointerEvents="none">
          {node.pctOfSignups.toFixed(0)}%
        </text>
      )}
    </>
  );
}

export default function SankeyChart({ nodes: nodesIn = [], links = [], height = 300, className = "", onNodeClick }) {
  const [tip, setTip] = useState(null);
  const nodes = nodesIn.length ? nodesIn : [];

  const layout = useMemo(() => {
    if (!nodes.length) return { positioned: [], w: 720, chartH: 0, padTop: 44, padBottom: 20, max: 1 };
    const max = Math.max(1, nodes[0]?.value ?? 1);
    const cols = Math.max(...nodes.map((n) => n.column)) + 1;
    const w = Math.max(760, 120 + cols * 108);
    const h = height;
    const padX = 18;
    const padTop = 44;
    const padBottom = 20;
    const chartH = h - padTop - padBottom;
    const nodeW = 10;
    const colGap = (w - padX * 2 - nodeW * cols) / Math.max(1, cols - 1);
    const positioned = layoutNodes(nodes, max, chartH, padTop, padX, nodeW, colGap);
    return { positioned, w, h, chartH, padTop, padBottom, max, nodeW };
  }, [nodes, height]);

  if (!nodes.length) {
    return <div className={`sankey-chart sankey-chart--empty ${className}`}>No funnel data</div>;
  }

  const nodeMap = Object.fromEntries(layout.positioned.map((n) => [n.id, n]));

  const showNodeTip = (node, e) => {
    const host = e.currentTarget.closest(".sankey-chart");
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const lines = [`${node.value.toLocaleString()} users`];
    if (node.pctOfSignups != null) lines.push(`${node.pctOfSignups.toFixed(1)}% of sign-ups`);
    if (node.optional) lines.push("Optional step");
    setTip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      placement: "above",
      title: node.label,
      lines,
    });
  };

  const showLinkTip = (link, e) => {
    const host = e.currentTarget.closest(".sankey-chart");
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const lines = [`${link.value.toLocaleString()} continued`];
    if (link.stepConvPct != null) lines.push(`${link.stepConvPct.toFixed(1)}% of previous step`);
    if (link.dropped > 0 && link.dropPct != null) lines.push(`${link.dropped.toLocaleString()} dropped (−${link.dropPct.toFixed(1)}%)`);
    if (link.optional) lines.push("Optional branch");
    setTip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      placement: "above",
      title: `${link.fromLabel || link.source} → ${link.toLabel || link.target}`,
      lines,
    });
  };

  return (
    <div className={`sankey-chart ${className}`} style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${layout.w} ${layout.h}`} className="sankey-chart-svg" role="img" aria-label="Activation funnel">
        {links.map((link) => {
          const a = nodeMap[link.source];
          const b = nodeMap[link.target];
          if (!a || !b || link.value <= 0) return null;
          const flowH = Math.max(5, (link.value / layout.max) * layout.chartH);
          const ay = a.y + (a.nodeH - flowH) / 2;
          const by = b.y + (b.nodeH - flowH) / 2;
          return (
            <path
              key={`flow-${link.source}-${link.target}`}
              d={linkPath(a.x + a.nodeW, ay, flowH, b.x, by, flowH)}
              fill={b.color || "var(--chart-line)"}
              opacity={link.optional ? "0.28" : "0.42"}
              className="sankey-link"
              onMouseEnter={(e) => showLinkTip(link, e)}
              onMouseMove={(e) => showLinkTip(link, e)}
              onMouseLeave={() => setTip(null)}
            />
          );
        })}

        {links.map((link) => {
          if (link.optional || link.dropped <= 0) return null;
          const a = nodeMap[link.source];
          if (!a) return null;
          const dropH = Math.max(4, (link.dropped / layout.max) * layout.chartH);
          const flowH = Math.max(5, (link.value / layout.max) * layout.chartH);
          const ay = a.y + (a.nodeH + flowH) / 2;
          return (
            <path
              key={`drop-${link.source}-${link.target}`}
              d={dropPath(a.x + a.nodeW / 2, ay - flowH / 2, flowH, dropH, layout.h - 8)}
              fill="var(--text-3)"
              opacity="0.2"
            />
          );
        })}

        {layout.positioned.map((node) => (
          <g key={node.id}>
            <rect
              x={node.x}
              y={node.y}
              width={node.nodeW}
              height={node.nodeH}
              rx={3}
              fill={node.color || "var(--chart-line)"}
              className={`sankey-node${onNodeClick ? " sankey-node--clickable" : ""}`}
              style={{ cursor: onNodeClick ? "pointer" : "crosshair" }}
              onMouseEnter={(e) => showNodeTip(node, e)}
              onMouseMove={(e) => showNodeTip(node, e)}
              onMouseLeave={() => setTip(null)}
              onClick={
                onNodeClick
                  ? (e) => {
                      e.stopPropagation();
                      onNodeClick(node);
                    }
                  : undefined
              }
            />
            <NodeLabels node={node} layout={layout} clickable={Boolean(onNodeClick)} onActivate={onNodeClick} />
          </g>
        ))}
      </svg>

      <div className="sankey-chart-legend">
        <span><i className="sankey-legend-flow" /> Main path</span>
        <span><i className="sankey-legend-optional" /> Optional profile branch</span>
        <span><i className="sankey-legend-drop" /> Dropped at step</span>
        {onNodeClick ? <span>Click a stage label to view users</span> : null}
      </div>
      <ChartTooltip tip={tip} />
    </div>
  );
}
